import React, { useEffect, useRef, useState } from 'react';
import { dragCrop } from './cropGeometry';
import './ImageCropDialog.css';

export default function ImageCropDialog({ source, onApply, onCancel, aspectRatio }) {
  const dialog = useRef(null);
  const canvas = useRef(null);
  const drag = useRef(null);
  const [image, setImage] = useState(null);
  const [crop, setCrop] = useState(null);
  const [error, setError] = useState('');
  const [applying, setApplying] = useState(false);
  const previewWidth = image ? Math.min(520, 280 * image.naturalWidth / image.naturalHeight) : 520;

  useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement;
    element.showModal();
    return () => {
      element.close();
      previousFocus?.focus();
    };
  }, []);

  useEffect(() => {
    let active = true;
    const local = source instanceof File;
    const url = local ? URL.createObjectURL(source) : source;
    const photo = new Image();
    if (!local) photo.crossOrigin = 'anonymous';
    photo.onload = () => {
      if (active) {
        setImage(photo);
        const ratio = aspectRatio || photo.naturalWidth / photo.naturalHeight;
        const width = Math.min(photo.naturalWidth, photo.naturalHeight * ratio);
        const height = width / ratio;
        setCrop({ x: (photo.naturalWidth - width) / 2, y: (photo.naturalHeight - height) / 2, width, height });
      }
    };
    photo.onerror = () => {
      if (active) setError('This image could not be opened for cropping. Please select a new JPG, PNG, or WebP image.');
    };
    photo.src = url;
    return () => {
      active = false;
      if (local) URL.revokeObjectURL(url);
    };
  }, [source, aspectRatio]);

  useEffect(() => {
    if (!image) return;
    const surface = canvas.current;
    surface.width = Math.max(1, Math.round(previewWidth));
    surface.height = Math.max(1, Math.round(surface.width * image.naturalHeight / image.naturalWidth));
    const context = surface.getContext('2d');
    context.clearRect(0, 0, surface.width, surface.height);
    context.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, surface.width, surface.height);
  }, [image, previewWidth]);

  const moveSelection = (event) => {
    const start = drag.current;
    if (!start || !image || applying) return;
    const bounds = canvas.current.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const dx = (event.clientX - start.x) * image.naturalWidth / bounds.width;
    const dy = (event.clientY - start.y) * image.naturalHeight / bounds.height;
    setCrop(dragCrop(start.crop, image.naturalWidth, image.naturalHeight, start.handle, dx, dy));
  };

  const keyboardCrop = (event, handle) => {
    if (applying || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    const step = Math.max(image.naturalWidth, image.naturalHeight) / (event.shiftKey ? 20 : 100);
    const dx = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
    const dy = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0;
    setCrop(dragCrop(crop, image.naturalWidth, image.naturalHeight, handle, dx, dy));
  };

  const applyCrop = async () => {
    setApplying(true);
    setError('');
    try {
      const output = document.createElement('canvas');
      const scale = Math.min(1, 1600 / Math.max(crop.width, crop.height));
      output.width = Math.max(1, Math.round(crop.width * scale));
      output.height = Math.max(1, Math.round(crop.height * scale));
      const context = output.getContext('2d');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, output.width, output.height);
      context.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, output.width, output.height);
      const blob = await new Promise((resolve, reject) => output.toBlob(
        result => result ? resolve(result) : reject(new Error('Image export failed')), 'image/jpeg', 0.92
      ));
      const name = source instanceof File ? source.name.replace(/\.[^.]+$/, '') : 'business-image';
      onApply(new File([blob], `${name}-cropped.jpg`, { type: 'image/jpeg' }));
    } catch (err) {
      setError('Could not crop this image. Please try again or select another image.');
      setApplying(false);
    }
  };

  return (
    <dialog ref={dialog} className="business-crop-dialog" aria-labelledby="business-crop-title"
      onCancel={event => { event.preventDefault(); if (!applying) onCancel(); }}>
      <h2 id="business-crop-title">Crop image</h2>
      <p>Drag the box to move it, or a corner to resize it. {aspectRatio === 1 ? 'Gallery crops stay square.' : 'The image proportions stay the same.'} Arrow keys work too. Changes upload when you save the business.</p>
      {!image && !error && <p role="status">Loading image…</p>}
      {error && <p role="alert" className="business-crop-error">{error}</p>}
      <div className="business-crop-stage" role="group" aria-label="Image crop controls" hidden={!image} style={{ width: previewWidth }}
        onPointerDown={event => {
          if (applying || !image || event.button !== 0 || !event.target.closest('[data-crop-handle]')) return;
          event.preventDefault();
          drag.current = { x: event.clientX, y: event.clientY, crop, handle: event.target.closest('[data-crop-handle]').dataset.cropHandle };
          event.currentTarget.setPointerCapture(event.pointerId);
        }} onPointerMove={moveSelection} onPointerUp={event => {
          moveSelection(event);
          drag.current = null;
        }} onPointerCancel={() => { drag.current = null; }} onLostPointerCapture={() => { drag.current = null; }}>
        <canvas ref={canvas} role="img" aria-label="Image with crop selection" />
        {crop && <div className="business-crop-selection" data-crop-handle="move" role="group"
          aria-label="Selected crop area" tabIndex={applying ? -1 : 0} onKeyDown={event => keyboardCrop(event, 'move')}
          style={{ left: `${crop.x / image.naturalWidth * 100}%`, top: `${crop.y / image.naturalHeight * 100}%`,
            width: `${crop.width / image.naturalWidth * 100}%`, height: `${crop.height / image.naturalHeight * 100}%` }}>
          {['nw', 'ne', 'sw', 'se'].map((handle, index) => <button key={handle} type="button"
            className={`business-crop-handle ${handle}`} data-crop-handle={handle} disabled={applying}
            aria-label={`Resize crop from ${['top left', 'top right', 'bottom left', 'bottom right'][index]}`}
            onKeyDown={event => keyboardCrop(event, handle)} />)}
        </div>}
      </div>
      <div className="business-crop-actions">
        <button type="button" onClick={onCancel} disabled={applying}>Cancel</button>
        <button type="button" onClick={applyCrop} disabled={!image || applying}>{applying ? 'Applying…' : 'Apply Crop'}</button>
      </div>
    </dialog>
  );
}
